import * as Sentry from '@sentry/vue'
import { createStore } from 'framework7/lite'

import type Reservation from '@/assets/classes/parking/Reservation'
import type Vehicle from '@/assets/classes/parking/Vehicle'
import type {
  BanInfo,
  CardLoanData,
  DefaultSettings,
  StoreState,
  User,
} from '@/assets/composables/types'

const store = createStore({
  state: {
    defaultSettings: {},
    isUserLoggedIn: false,
    reservations: [],
    historicalReservations: [],
    cardMissingReservations: [],
    cardLoans: [],
    cardLoanTotal: 0,
    vehicles: [],
    user: null,
    banInfo: null,
    updateRequired: false,
  } as StoreState,

  getters: {
    user({ state }: { state: StoreState }) {
      return state.user
    },
    isUserLoggedIn({ state }: { state: StoreState }) {
      return state.isUserLoggedIn
    },
    defaultSettings({ state }: { state: StoreState }) {
      return state.defaultSettings
    },
    reservations({ state }: { state: StoreState }) {
      return state.reservations
    },
    historicalReservations({ state }: { state: StoreState }) {
      return state.historicalReservations
    },
    cardMissingReservations({ state }: { state: StoreState }) {
      return state.cardMissingReservations
    },
    cardLoans({ state }: { state: StoreState }) {
      return state.cardLoans
    },
    cardLoanTotal({ state }: { state: StoreState }) {
      return state.cardLoanTotal
    },
    vehicles({ state }: { state: StoreState }) {
      return state.vehicles
    },
    banInfo({ state }: { state: StoreState }) {
      return state.banInfo
    },
  },

  actions: {
    setUser({ state }: { state: StoreState }, user: User | null) {
      Sentry.setUser(user ? { id: user.id } : null)
      state.user = user
      state.isUserLoggedIn = !!user
    },
    setDefaultSettings({ state }: { state: StoreState }, settings: DefaultSettings) {
      state.defaultSettings = settings
    },
    setReservations({ state }: { state: StoreState }, reservations: Reservation[]) {
      state.reservations = reservations
    },
    setHistoricalReservations({ state }: { state: StoreState }, reservations: Reservation[]) {
      state.historicalReservations = reservations
    },
    setCardMissingReservations({ state }: { state: StoreState }, reservations: Reservation[]) {
      state.cardMissingReservations = reservations
    },
    setCardLoans(
      { state }: { state: StoreState },
      data: { rows: CardLoanData[]; total: number; append?: boolean },
    ) {
      state.cardLoans = data.append
        ? [...new Map([...state.cardLoans, ...data.rows].map((loan) => [loan.id, loan])).values()]
        : data.rows
      state.cardLoanTotal = data.total
    },
    removeCardLoan({ state }: { state: StoreState }, id: string) {
      if (state.cardLoans.some((loan) => loan.id === id)) {
        state.cardLoans = state.cardLoans.filter((loan) => loan.id !== id)
        state.cardLoanTotal = Math.max(0, state.cardLoanTotal - 1)
      }
    },
    setVehicles({ state }: { state: StoreState }, vehicles: Vehicle[]) {
      state.vehicles = vehicles
    },
    setBanInfo({ state }: { state: StoreState }, banInfo: BanInfo | null) {
      state.banInfo = banInfo
    },
  },
})
export default store
